import { action, makeObservable, observable, runInAction } from 'mobx'
import { IMercatusAxiosRequestConfig } from '@migrate/utils/axiosInterceptor'
import axios, { Method, AxiosHeaders } from 'axios'
import { Link } from '@portal/pkg-api/gen/shared'
import { data, HypermediaApi } from '@portal/pkg-api/shared'
import SecurityStore from '@migrate/stores/SecurityStore'

export class HyperMediaStore {
  public static STORE_NAME = 'hyperMediaStore'

  @observable public ready: boolean
  @observable public links: Link[]

  constructor() {
    this.ready = false
    this.links = []
    makeObservable(this)
  }

  public linkByRef(ref: string) {
    return this.ready ? this.links.find((l) => l.rel === ref) : undefined
  }

  public async fetchRemoteResource(
    ref: string,
    tokens?: { [key: string]: any },
    customHeaders?: any,
    mercatusNotifyOnError: boolean = true,
  ) {
    const link = this.linkByRef(ref)
    if (link) {
      const url = tokens ? this.replaceURITokens(link.href, tokens) : link.href
      const headers = new AxiosHeaders(customHeaders || { 'Cache-Control': 'no-cache' })
      const reqConfig: IMercatusAxiosRequestConfig = SecurityStore.addRequestHeaders({
        method: 'GET',
        withCredentials: true,
        headers,
        url,
        mercatusNotifyOnError,
      })
      const response = await axios.request(reqConfig)
      return response.data
    }
  }

  public async createRemoteResource(
    ref: string,
    value: any,
    tokens?: object,
    mercatusNotifyOnError: boolean = true,
  ) {
    return this.modifyRemoteResource('POST', ref, value, tokens, mercatusNotifyOnError)
  }

  public async saveRemoteResource(
    ref: string,
    value: any,
    tokens?: object,
    mercatusNotifyOnError: boolean = true,
  ) {
    return this.modifyRemoteResource('PUT', ref, value, tokens, mercatusNotifyOnError)
  }

  public async patchRemoteResource(
    ref: string,
    value: any,
    tokens?: object,
    mercatusNotifyOnError: boolean = true,
  ) {
    return this.modifyRemoteResource('PATCH', ref, value, tokens, mercatusNotifyOnError)
  }

  public async deleteRemoteResource(
    ref: string,
    tokens?: object,
    mercatusNotifyOnError: boolean = true,
  ) {
    return this.modifyRemoteResource('DELETE', ref, null, tokens, mercatusNotifyOnError)
  }

  public async modifyRemoteResource(
    method: Method,
    ref: string,
    value: any,
    tokens?: object,
    mercatusNotifyOnError: boolean = true,
  ) {
    const link = this.linkByRef(ref)
    if (link) {
      const url = tokens ? this.replaceURITokens(link.href, tokens) : link.href
      const reqConfig: IMercatusAxiosRequestConfig = {
        method,
        withCredentials: true,
        sendCsrfToken: false,
        headers: new AxiosHeaders({ 'Cache-Control': 'no-cache' }),
        url,
        data: value,
        mercatusNotifyOnError,
      }
      const response = await axios.request(reqConfig)
      return response.data
    }
  }

  public async uploadRemoteResource(
    ref: string,
    value: any,
    tokens?: object,
    contentType?: string,
    mercatusNotifyOnError: boolean = true,
    callback?: (n: number) => any,
  ) {
    const link = this.linkByRef(ref)
    if (link) {
      const url = tokens ? this.replaceURITokens(link.href, tokens) : link.href
      const reqConfig: IMercatusAxiosRequestConfig = {
        method: 'POST',
        withCredentials: true,
        sendCsrfToken: false,
        headers: new AxiosHeaders({
          'Content-Type': contentType ? contentType : 'multipart/form-data',
        }),
        onUploadProgress: (progressEvent: any) => {
          if (callback) {
            callback(Math.round((progressEvent.loaded * 100) / progressEvent.total))
          }
        },
        url,
        data: value,
        mercatusNotifyOnError,
      }
      const response = await axios.request(reqConfig)
      return response.data
    }
  }

  public replaceURITokens(URI: string, tokens: any) {
    let url = decodeURI(URI)
    Object.keys(tokens).forEach(function (key) {
      url = url.replace('{' + key + '}', encodeURIComponent(tokens[key]))
    })
    return url
  }

  @action
  async load() {
    this.links = await data(HypermediaApi.getLinks())
    runInAction(() => {
      this.ready = true
    })
  }

  @action
  unload() {
    this.links = []
  }
}
